import {
  Table,
  Button,
  Space,
  Tag,
  Input,
  Select,
  theme,
  Typography,
  Form,
  Modal,
  Tabs,
  DatePicker
} from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState, useRef } from "react";
import { CSVLink } from "react-csv";

import { showErrorNotification, showSuccessNotification } from "../../utils/notification";

const { RangePicker } = DatePicker;
const { Text } = Typography;
const { useToken } = theme;

const GUIDANCE_SESSION_STATUS = {
  UPCOMING: "UPCOMING",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
  NO_SHOW: "NO_SHOW"
};

const GUIDANCE_SESSION_CANCELLATION_REASON = {
  CANCELLED_BY_STUDENT: "CANCELLED_BY_STUDENT",
  CANCELLED_BY_STUDENT_WITH_REFUND: "CANCELLED_BY_STUDENT_WITH_REFUND",
  CANCELLED_BY_MENTOR: "CANCELLED_BY_MENTOR",
  NO_SHOW_BY_STUDENT: "NO_SHOW_BY_STUDENT",
  NO_SHOW_BY_MENTOR: "NO_SHOW_BY_MENTOR"
};

const GUIDANCE_SESSION_OUTCOME_CONFIG = {
  [GUIDANCE_SESSION_STATUS.UPCOMING]: {
    label: "Upcoming",
    color: "blue"
  },
  [GUIDANCE_SESSION_STATUS.COMPLETED]: {
    label: "Completed",
    color: "green"
  },
  [GUIDANCE_SESSION_STATUS.CANCELLED]: {
    label: "Cancelled",
    color: "red"
  },
  [GUIDANCE_SESSION_STATUS.NO_SHOW]: {
    label: "No Show",
    color: "volcano"
  },
  [GUIDANCE_SESSION_CANCELLATION_REASON.CANCELLED_BY_STUDENT]: {
    label: "Cancelled by Student",
    color: "red"
  },
  [GUIDANCE_SESSION_CANCELLATION_REASON.CANCELLED_BY_STUDENT_WITH_REFUND]: {
    label: "Cancelled by Student (Refunded)",
    color: "orange"
  },
  [GUIDANCE_SESSION_CANCELLATION_REASON.CANCELLED_BY_MENTOR]: {
    label: "Cancelled by Mentor (Refunded)",
    color: "orange"
  },
  [GUIDANCE_SESSION_CANCELLATION_REASON.NO_SHOW_BY_STUDENT]: {
    label: "No Show by Student",
    color: "volcano"
  },
  [GUIDANCE_SESSION_CANCELLATION_REASON.NO_SHOW_BY_MENTOR]: {
    label: "No Show by Mentor (Refunded)",
    color: "gold"
  }
};

const GuidanceSessionsDash = ({ userConfig, ROOT_URL }) => {
  const hasManagementAccess = userConfig?.guidanceSessions?.hasManagementAccess || false;
  const { token } = useToken();

  const BASE_URL = "admin/guidance-sessions";

  const [sessionsData, setSessionsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalSessions, setTotalSessions] = useState(0);

  // Filter States
  const [studentSearch, setStudentSearch] = useState("");
  const [mentorSearch, setMentorSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [bookingTimeRange, setBookingTimeRange] = useState(null);
  const [sessionTimeRange, setSessionTimeRange] = useState(null);

  // Query state passed to fetch
  const [queryFilters, setQueryFilters] = useState({
    searchUserEmail: "",
    searchMentorEmail: "",
    status: "ALL",
    bookingTimeStart: null,
    bookingTimeEnd: null,
    sessionTimeStart: null,
    sessionTimeEnd: null
  });

  const [mentorsList, setMentorsList] = useState([]);
  const [mentorsLoading, setMentorsLoading] = useState(false);
  const [isAddMentorModalOpen, setIsAddMentorModalOpen] = useState(false);
  const [addMentorForm] = Form.useForm();

  const [sessionsCSVData, setSessionsCSVData] = useState([]);
  const csvLinkRef = useRef(null);

  const fetchMentors = () => {
    setMentorsLoading(true);
    axios
      .get(`${ROOT_URL}/${BASE_URL}/mentors/list`)
      .then((res) => {
        setMentorsList(res.data);
        setMentorsLoading(false);
      })
      .catch((err) => {
        showErrorNotification(err);
        setMentorsLoading(false);
      });
  };

  const handleAddMentor = (values) => {
    axios
      .post(`${ROOT_URL}/${BASE_URL}/mentors/add`, { email: values.email })
      .then(() => {
        showSuccessNotification("Mentor added successfully");
        setIsAddMentorModalOpen(false);
        addMentorForm.resetFields();
        fetchMentors();
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  const handleRemoveMentor = (email) => {
    axios
      .post(`${ROOT_URL}/${BASE_URL}/mentors/remove`, { email })
      .then(() => {
        showSuccessNotification("Mentor access revoked successfully");
        fetchMentors();
      })
      .catch((err) => {
        showErrorNotification(err);
      });
  };

  /**
   * Fetch paginated sessions
   */
  const fetchSessions = async (page = currentPage, limit = pageSize, filters = queryFilters) => {
    setLoading(true);
    try {
      const params = {
        page,
        limit
      };

      if (filters.searchUserEmail) {
        params.searchUserEmail = filters.searchUserEmail;
      }
      if (filters.searchMentorEmail) {
        params.searchMentorEmail = filters.searchMentorEmail;
      }
      if (filters.status && filters.status !== "ALL") {
        params.status = filters.status;
      }
      if (filters.bookingTimeStart) params.bookingTimeStart = filters.bookingTimeStart;
      if (filters.bookingTimeEnd) params.bookingTimeEnd = filters.bookingTimeEnd;
      if (filters.sessionTimeStart) params.sessionTimeStart = filters.sessionTimeStart;
      if (filters.sessionTimeEnd) params.sessionTimeEnd = filters.sessionTimeEnd;

      const res = await axios.get(`${ROOT_URL}/${BASE_URL}/list`, { params });
      const { sessions, totalItems } = res.data;

      setSessionsData(
        (sessions || []).map((session, idx) => ({
          ...session,
          key: session.uniquePublicId,
          index: (page - 1) * limit + idx + 1
        }))
      );
      setTotalSessions(totalItems || 0);
    } catch (err) {
      showErrorNotification(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions(currentPage, pageSize, queryFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, queryFilters]);

  const handleSearch = () => {
    setCurrentPage(1);
    setQueryFilters({
      searchUserEmail: studentSearch.trim(),
      searchMentorEmail: mentorSearch.trim(),
      status: statusFilter,
      bookingTimeStart: bookingTimeRange ? bookingTimeRange[0].toISOString() : null,
      bookingTimeEnd: bookingTimeRange ? bookingTimeRange[1].toISOString() : null,
      sessionTimeStart: sessionTimeRange ? sessionTimeRange[0].toISOString() : null,
      sessionTimeEnd: sessionTimeRange ? sessionTimeRange[1].toISOString() : null
    });
  };

  const handleReset = () => {
    setStudentSearch("");
    setMentorSearch("");
    setStatusFilter("ALL");
    setBookingTimeRange(null);
    setSessionTimeRange(null);
    setCurrentPage(1);
    setQueryFilters({
      searchUserEmail: "",
      searchMentorEmail: "",
      status: "ALL",
      bookingTimeStart: null,
      bookingTimeEnd: null,
      sessionTimeStart: null,
      sessionTimeEnd: null
    });
  };

  const handleDownloadCsv = async () => {
    try {
      const params = {};
      if (queryFilters.searchUserEmail) {
        params.searchUserEmail = queryFilters.searchUserEmail;
      }
      if (queryFilters.searchMentorEmail) {
        params.searchMentorEmail = queryFilters.searchMentorEmail;
      }
      if (queryFilters.status && queryFilters.status !== "ALL") {
        params.status = queryFilters.status;
      }
      if (queryFilters.bookingTimeStart) {
        params.bookingTimeStart = queryFilters.bookingTimeStart;
      }
      if (queryFilters.bookingTimeEnd) {
        params.bookingTimeEnd = queryFilters.bookingTimeEnd;
      }
      if (queryFilters.sessionTimeStart) {
        params.sessionTimeStart = queryFilters.sessionTimeStart;
      }
      if (queryFilters.sessionTimeEnd) {
        params.sessionTimeEnd = queryFilters.sessionTimeEnd;
      }

      const res = await axios.get(`${ROOT_URL}/${BASE_URL}/export-csv`, { params });
      const mappedData = (res.data || []).map(
        (
          {
            sessionStatus,
            cancellationReason,
            studentName,
            studentEmail,
            mentorName,
            mentorEmail,
            bookingTime,
            sessionStartTime,
            googleMeetLink,
            recordingUrl,
            levelEnrolled,
            codeforcesHandle,
            bookingTopic
          },
          index
        ) => {
          const info = getStatusLabelAndColor(sessionStatus, cancellationReason);
          return {
            "S No.": index + 1,
            "Student Name": studentName || "N/A",
            "Student Email": studentEmail || "N/A",
            "Mentor Name": mentorName || "N/A",
            "Mentor Email": mentorEmail || "N/A",
            "Booking Time": bookingTime ? dayjs(bookingTime).format("YYYY-MM-DD HH:mm") : "N/A",
            "Session Time": sessionStartTime
              ? dayjs(sessionStartTime).format("YYYY-MM-DD HH:mm")
              : "N/A",
            "Meet Link": googleMeetLink || "N/A",
            Recording: recordingUrl || "N/A",
            "Level Enrolled": levelEnrolled || "N/A",
            "CF Handle": codeforcesHandle || "N/A",
            "Booking Topic": bookingTopic || "N/A",
            Status: info.label
          };
        }
      );
      setSessionsCSVData(mappedData);
      showSuccessNotification("CSV downloaded successfully");
    } catch (err) {
      showErrorNotification(err);
    }
  };

  useEffect(() => {
    if (sessionsCSVData?.length > 0 && csvLinkRef.current) {
      csvLinkRef.current.link.click();
    }
  }, [sessionsCSVData]);

  const getStatusLabelAndColor = (status, reason) => {
    if (reason && GUIDANCE_SESSION_OUTCOME_CONFIG[reason]) {
      return GUIDANCE_SESSION_OUTCOME_CONFIG[reason];
    }
    if (GUIDANCE_SESSION_OUTCOME_CONFIG[status]) {
      return GUIDANCE_SESSION_OUTCOME_CONFIG[status];
    }
    return { label: status, color: "default" };
  };

  const columns = [
    {
      title: "S No.",
      dataIndex: "index",
      width: 70
    },
    {
      title: "Student Name",
      dataIndex: "studentName",
      width: 150,
      render: (text) => text || "N/A"
    },
    {
      title: "Student Email",
      dataIndex: "studentEmail",
      width: 220
    },
    {
      title: "Mentor Name",
      dataIndex: "mentorName",
      width: 150,
      render: (text) => text || "N/A"
    },
    {
      title: "Mentor Email",
      dataIndex: "mentorEmail",
      width: 220
    },
    {
      title: "Booking Time",
      dataIndex: "bookingTime",
      width: 180,
      render: (time) => (time ? dayjs(time).format("YYYY-MM-DD HH:mm") : "N/A")
    },
    {
      title: "Session Time",
      dataIndex: "sessionStartTime",
      width: 180,
      render: (time) => (time ? dayjs(time).format("YYYY-MM-DD HH:mm") : "N/A")
    },
    {
      title: "Meet Link",
      dataIndex: "googleMeetLink",
      width: 250,
      render: (link) =>
        link ? (
          <Button type="link" href={link} target="_blank" style={{ padding: 0 }}>
            Meet Link
          </Button>
        ) : (
          "N/A"
        )
    },
    {
      title: "Recording",
      dataIndex: "recordingUrl",
      width: 150,
      render: (link) =>
        link ? (
          <Button type="link" href={link} target="_blank" style={{ padding: 0 }}>
            View Recording
          </Button>
        ) : (
          "N/A"
        )
    },
    {
      title: "Level Enrolled",
      dataIndex: "levelEnrolled",
      width: 150,
      render: (text) => text || "N/A"
    },
    {
      title: "CF Handle",
      dataIndex: "codeforcesHandle",
      width: 150,
      render: (text) => text || "N/A"
    },
    {
      title: "Booking Topic",
      dataIndex: "bookingTopic",
      width: 250,
      render: (text) => text || "N/A"
    },
    {
      title: "Status",
      dataIndex: "sessionStatus",
      width: 200,
      render: (status, row) => {
        const info = getStatusLabelAndColor(status, row.cancellationReason);
        return <Tag color={info.color}>{info.label}</Tag>;
      }
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          background: token.colorBgContainer,
          padding: 20,
          borderRadius: 8,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)"
        }}
      >
        <Tabs
          defaultActiveKey="sessions"
          onChange={(key) => {
            if (key === "mentors") {
              fetchMentors();
            }
          }}
          items={[
            {
              key: "sessions",
              label: "Guidance Sessions",
              children: (
                <>
                  {/* Title Block */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 24,
                      alignItems: "center"
                    }}
                  >
                    <div>
                      <h2 style={{ margin: 0 }}>Guidance Sessions</h2>
                      <Text type="secondary">Monitor and audit student 1:1 sessions</Text>
                    </div>

                    <CSVLink
                      asyncOnClick={true}
                      data={sessionsCSVData || []}
                      ref={csvLinkRef}
                      filename={`guidance-sessions_${dayjs().format("YYYY-MM-DD")}.csv`}
                    />
                    <Button
                      type="primary"
                      onClick={handleDownloadCsv}
                      disabled={!hasManagementAccess}
                    >
                      Export CSV
                    </Button>
                  </div>

                  {/* Filters Block */}
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "12px",
                      marginBottom: 20,
                      alignItems: "center"
                    }}
                  >
                    <Input
                      placeholder="Student Email"
                      value={studentSearch}
                      onChange={(e) => setStudentSearch(e.target.value)}
                      style={{ width: 220 }}
                      onPressEnter={handleSearch}
                    />
                    <Input
                      placeholder="Mentor Email"
                      value={mentorSearch}
                      onChange={(e) => setMentorSearch(e.target.value)}
                      style={{ width: 220 }}
                      onPressEnter={handleSearch}
                    />
                    <Select
                      value={statusFilter}
                      onChange={setStatusFilter}
                      style={{ width: 220 }}
                      options={[
                        { label: "All Outcomes", value: "ALL" },
                        { label: "Upcoming / Scheduled", value: GUIDANCE_SESSION_STATUS.UPCOMING },
                        { label: "Completed", value: GUIDANCE_SESSION_STATUS.COMPLETED },
                        {
                          label: "Cancelled by Student",
                          value: GUIDANCE_SESSION_CANCELLATION_REASON.CANCELLED_BY_STUDENT
                        },
                        {
                          label: "Cancelled by Student (Refunded)",
                          value: GUIDANCE_SESSION_CANCELLATION_REASON.CANCELLED_BY_STUDENT_WITH_REFUND
                        },
                        {
                          label: "Cancelled by Mentor",
                          value: GUIDANCE_SESSION_CANCELLATION_REASON.CANCELLED_BY_MENTOR
                        },
                        {
                          label: "No Show by Student",
                          value: GUIDANCE_SESSION_CANCELLATION_REASON.NO_SHOW_BY_STUDENT
                        },
                        {
                          label: "No Show by Mentor",
                          value: GUIDANCE_SESSION_CANCELLATION_REASON.NO_SHOW_BY_MENTOR
                        }
                      ]}
                    />
                    <RangePicker
                      value={bookingTimeRange}
                      onChange={setBookingTimeRange}
                      placeholder={["Booking Start", "Booking End"]}
                      showTime
                      style={{ width: 320 }}
                    />
                    <RangePicker
                      value={sessionTimeRange}
                      onChange={setSessionTimeRange}
                      placeholder={["Session Start", "Session End"]}
                      showTime
                      style={{ width: 320 }}
                    />
                    <Space>
                      <Button type="primary" onClick={handleSearch}>
                        Apply Filters
                      </Button>
                      <Button onClick={handleReset}>Reset</Button>
                    </Space>
                  </div>

                  {/* Table Block */}
                  <Table
                    columns={columns}
                    dataSource={sessionsData}
                    loading={loading}
                    bordered
                    pagination={{
                      current: currentPage,
                      pageSize: pageSize,
                      total: totalSessions,
                      onChange: (page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                      },
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "20", "50"]
                    }}
                    scroll={{ x: 1200 }}
                  />
                </>
              )
            },
            {
              key: "mentors",
              label: "Manage Mentors",
              children: (
                <>
                  {/* Title Block */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 24,
                      alignItems: "center"
                    }}
                  >
                    <div>
                      <h2 style={{ margin: 0 }}>Mentors List</h2>
                      <Text type="secondary">
                        Manage users with guidance sessions mentor access
                      </Text>
                    </div>

                    <Button
                      type="primary"
                      onClick={() => setIsAddMentorModalOpen(true)}
                      disabled={!hasManagementAccess}
                    >
                      Add New Mentor
                    </Button>
                  </div>

                  <Table
                    loading={mentorsLoading}
                    dataSource={mentorsList}
                    rowKey="email"
                    bordered
                    columns={[
                      {
                        title: "Name",
                        dataIndex: "name",
                        key: "name"
                      },
                      {
                        title: "Email",
                        dataIndex: "email",
                        key: "email"
                      },
                      {
                        title: "Actions",
                        key: "actions",
                        width: 150,
                        render: (row) => (
                          <Button
                            danger
                            type="primary"
                            onClick={() => handleRemoveMentor(row.email)}
                            disabled={!hasManagementAccess}
                          >
                            Revoke Access
                          </Button>
                        )
                      }
                    ]}
                  />

                  <Modal
                    title="Add New Mentor"
                    open={isAddMentorModalOpen}
                    onCancel={() => setIsAddMentorModalOpen(false)}
                    onOk={() => addMentorForm.submit()}
                    destroyOnClose
                  >
                    <Form
                      form={addMentorForm}
                      layout="vertical"
                      onFinish={handleAddMentor}
                      style={{ marginTop: 16 }}
                    >
                      <Form.Item
                        name="email"
                        label="User Email"
                        rules={[
                          { required: true, message: "Please enter user email" },
                          { type: "email", message: "Please enter a valid email" }
                        ]}
                      >
                        <Input placeholder="mentor@tleeliminators.com" />
                      </Form.Item>
                    </Form>
                  </Modal>
                </>
              )
            }
          ]}
        />
      </div>
    </div>
  );
};

export default GuidanceSessionsDash;
